/**
 * Credential Definitions Utility
 * Central registry for credential type metadata including icons, colors, categories, and help content
 * Follows the pattern established by nodeDefinitions.ts
 */

import {
  Key,
  Shield,
  Lock,
  Code,
  Database,
  Cloud,
  Globe,
  Server,
  Mail,
  FileText,
  Banknote,
  Users,
  Brain,
  HardDrive,
  Plug,
  type LucideIcon,
} from 'lucide-react';

import {
  SiGooglecloud,
  SiOpenai,
  SiAnthropic,
  SiPostgresql,
  SiMysql,
  SiMongodb,
  SiRedis,
  SiAsana,
} from '@icons-pack/react-simple-icons';

import {
  AzureBlobStorage as AzureBlobStorageIcon,
  MicrosoftTeams as MicrosoftTeamsIcon,
  MicrosoftOutlook as MicrosoftOutlookIcon,
  MicrosoftOneDrive as OneDriveIcon,
  MicrosoftSharePoint as SharePointIcon,
  MicrosoftSqlServer as SqlServerIcon,
  MicrosoftAzure as AzureIcon,
  MicrosoftEntra as EntraIcon,
} from './microsoftIcons';

import { AsanaIcon } from './integrationIcons';

// ============================================================================
// Types
// ============================================================================

export type CredentialCategory =
  | 'general'
  | 'access-integrations'
  | 'ai-services'
  | 'databases'
  | 'cloud'
  | 'microsoft'
  | 'file-transfer'
  | 'email';

export interface CredentialHelpContent {
  description: string;
  setupSteps: string[];
  requiredFields: string[];
  links?: { label: string; url: string }[];
  tips?: string[];
}

export interface CredentialDefinition {
  type: string;
  label: string;
  description: string;
  category: CredentialCategory;
  icon: LucideIcon | React.ComponentType<any>;
  iconType: 'lucide' | 'simple-icon' | 'microsoft-icon' | 'integration-icon';
  iconColor?: string;
  bgColor: string;
  borderColor: string;
  color: string;
  tags: string[];
  testable: boolean;
  helpContent: CredentialHelpContent;
}

export interface CategoryDefinition {
  label: string;
  icon: LucideIcon;
  description: string;
}

// ============================================================================
// Color Palette (consistent with nodeDefinitions.ts)
// ============================================================================

const colorMap: Record<string, { color: string; bgColor: string; borderColor: string }> = {
  'orange': { color: '#f97316', bgColor: '#fff7ed', borderColor: '#fed7aa' },
  'amber': { color: '#f59e0b', bgColor: '#fffbeb', borderColor: '#fde68a' },
  'blue': { color: '#3b82f6', bgColor: '#eff6ff', borderColor: '#bfdbfe' },
  'yellow': { color: '#eab308', bgColor: '#fefce8', borderColor: '#fde047' },
  'green': { color: '#10b981', bgColor: '#f0fdf4', borderColor: '#bbf7d0' },
  'purple': { color: '#a855f7', bgColor: '#faf5ff', borderColor: '#e9d5ff' },
  'violet': { color: '#8b5cf6', bgColor: '#f5f3ff', borderColor: '#ddd6fe' },
  'red': { color: '#dc2626', bgColor: '#fef2f2', borderColor: '#fecaca' },
  'rose': { color: '#f43f5e', bgColor: '#fff1f2', borderColor: '#fecdd3' },
  'pink': { color: '#ec4899', bgColor: '#fdf2f8', borderColor: '#fbcfe8' },
  'indigo': { color: '#6366f1', bgColor: '#eef2ff', borderColor: '#c7d2fe' },
  'cyan': { color: '#06b6d4', bgColor: '#ecfeff', borderColor: '#a5f3fc' },
  'gray': { color: '#6b7280', bgColor: '#f9fafb', borderColor: '#d1d5db' },
  'emerald': { color: '#059669', bgColor: '#ecfdf5', borderColor: '#a7f3d0' },
  'teal': { color: '#14b8a6', bgColor: '#f0fdfa', borderColor: '#99f6e4' },
};

// ============================================================================
// Category Definitions
// ============================================================================

export const CREDENTIAL_CATEGORIES: Record<CredentialCategory, CategoryDefinition> = {
  'general': {
    label: 'General',
    icon: Key,
    description: 'API keys, tokens, and basic authentication',
  },
  'access-integrations': {
    label: 'Access Integrations',
    icon: Plug,
    description: 'Access platform services and integrations',
  },
  'ai-services': {
    label: 'AI Services',
    icon: Brain,
    description: 'AI and ML platform credentials',
  },
  'databases': {
    label: 'Databases',
    icon: Database,
    description: 'Database connection credentials',
  },
  'cloud': {
    label: 'Cloud Providers',
    icon: Cloud,
    description: 'AWS, Azure, GCP credentials',
  },
  'microsoft': {
    label: 'Microsoft 365',
    icon: Globe,
    description: 'Microsoft services and OAuth',
  },
  'file-transfer': {
    label: 'File Transfer',
    icon: HardDrive,
    description: 'FTP, SFTP, and cloud storage',
  },
  'email': {
    label: 'Email',
    icon: Mail,
    description: 'SMTP and email services',
  },
};

// ============================================================================
// Credential Definitions
// ============================================================================

export const CREDENTIAL_DEFINITIONS: Record<string, CredentialDefinition> = {
  // ---- General ----
  'api_key': {
    type: 'api_key',
    label: 'API Key',
    description: 'Simple API key authentication',
    category: 'general',
    icon: Key,
    iconType: 'lucide',
    ...colorMap['amber'],
    tags: ['api', 'key', 'authentication', 'token'],
    testable: false,
    helpContent: {
      description: 'Use a single API key for authentication with external services.',
      setupSteps: [
        'Navigate to your service provider\'s API settings',
        'Generate or copy your API key',
        'Paste the API key in the field below',
      ],
      requiredFields: ['API Key'],
      tips: ['Keep your API key secure and never share it publicly'],
    },
  },
  'bearer_token': {
    type: 'bearer_token',
    label: 'Bearer Token',
    description: 'Bearer token authentication',
    category: 'general',
    icon: Shield,
    iconType: 'lucide',
    ...colorMap['blue'],
    tags: ['bearer', 'token', 'oauth', 'jwt'],
    testable: false,
    helpContent: {
      description: 'Use a bearer token for API authentication. The token is sent in the Authorization header.',
      setupSteps: [
        'Obtain your bearer token from the service provider',
        'Paste the token in the field below',
      ],
      requiredFields: ['Token'],
      tips: ['Bearer tokens may expire - check your provider\'s documentation for token refresh procedures'],
    },
  },
  'basic_auth': {
    type: 'basic_auth',
    label: 'Basic Auth',
    description: 'Username and password authentication',
    category: 'general',
    icon: Lock,
    iconType: 'lucide',
    ...colorMap['gray'],
    tags: ['basic', 'username', 'password', 'http'],
    testable: false,
    helpContent: {
      description: 'HTTP Basic Authentication using username and password.',
      setupSteps: [
        'Enter your username',
        'Enter your password',
      ],
      requiredFields: ['Username', 'Password'],
      tips: ['Basic auth credentials are base64 encoded - always use HTTPS for secure transmission'],
    },
  },
  'api_key_header': {
    type: 'api_key_header',
    label: 'API Key Header',
    description: 'Custom header API key authentication',
    category: 'general',
    icon: Code,
    iconType: 'lucide',
    ...colorMap['purple'],
    tags: ['api', 'key', 'header', 'custom'],
    testable: false,
    helpContent: {
      description: 'Send an API key in a custom HTTP header.',
      setupSteps: [
        'Enter the header name (e.g., X-API-Key)',
        'Enter your API key value',
      ],
      requiredFields: ['Header Name', 'API Key'],
      tips: ['Common header names include X-API-Key, X-Auth-Token, or service-specific headers'],
    },
  },
  'oauth2': {
    type: 'oauth2',
    label: 'OAuth2',
    description: 'OAuth2 authentication flow',
    category: 'general',
    icon: Lock,
    iconType: 'lucide',
    ...colorMap['indigo'],
    tags: ['oauth', 'oauth2', 'authorization', 'token'],
    testable: false,
    helpContent: {
      description: 'OAuth2 authentication for secure API access.',
      setupSteps: [
        'Register your application with the OAuth provider',
        'Obtain your Client ID and Client Secret',
        'Configure the authorization and token URLs',
      ],
      requiredFields: ['Client ID', 'Client Secret'],
      tips: ['Store your client secret securely and never expose it in client-side code'],
    },
  },

  // ---- Access Integrations ----
  'data-engine': {
    type: 'data-engine',
    label: 'Data Engine',
    description: 'Access Data Engine API',
    category: 'access-integrations',
    icon: Database,
    iconType: 'lucide',
    ...colorMap['emerald'],
    tags: ['access', 'data', 'engine', 'api'],
    testable: true,
    helpContent: {
      description: 'Connect to the Access Data Engine API for data operations.',
      setupSteps: [
        'Obtain your Data Engine API key from your administrator',
        'Enter the API key in the field below',
      ],
      requiredFields: ['API Key'],
      tips: ['Contact your Access administrator if you need API access'],
    },
  },
  'accessDocument': {
    type: 'accessDocument',
    label: 'Access Document',
    description: 'Access Document Management System',
    category: 'access-integrations',
    icon: FileText,
    iconType: 'lucide',
    ...colorMap['teal'],
    tags: ['access', 'document', 'dms', 'files'],
    testable: true,
    helpContent: {
      description: 'Connect to Access Document Management System for document operations. You will need your Instance ID and Shared Secret from your Access Document administrator.',
      setupSteps: [
        'Contact your Access Document administrator',
        'Obtain your Instance ID (GUID format)',
        'Get your Shared Secret for HMAC-SHA256 API authentication',
        'Optionally configure a custom Web Service URL for on-premise deployments',
      ],
      requiredFields: ['Instance ID', 'Shared Secret'],
      links: [
        { label: 'Access Documentation', url: 'https://www.theaccessgroup.com/support/' },
      ],
      tips: ['The Web Service URL is optional and defaults to the standard Access Document endpoint'],
    },
  },
  'access-charity-crm': {
    type: 'access-charity-crm',
    label: 'Access Charity CRM (thankQ)',
    description: 'thankQ CRM integration',
    category: 'access-integrations',
    icon: Users,
    iconType: 'lucide',
    ...colorMap['pink'],
    tags: ['access', 'charity', 'crm', 'thankq', 'nonprofit'],
    testable: true,
    helpContent: {
      description: 'Connect to Access Charity CRM (thankQ) for donor and constituent management. Contact your Access administrator for credentials.',
      setupSteps: [
        'Contact your Access administrator for API credentials',
        'Obtain either an API Key or Bearer Token',
        'Configure the base URL if using a custom endpoint',
      ],
      requiredFields: ['API Key or Bearer Token'],
      tips: [
        'Provide at least one of: API Key or Bearer Token. Both can be used together.',
        'The Base URL is optional and defaults to the production thankQ API.',
      ],
    },
  },
  'access-agentic-platform': {
    type: 'access-agentic-platform',
    label: 'Access Agentic Platform',
    description: 'AI Agent Middleware integration',
    category: 'access-integrations',
    icon: Brain,
    iconType: 'lucide',
    ...colorMap['violet'],
    tags: ['access', 'agentic', 'ai', 'agent', 'middleware', 'roster'],
    testable: true,
    helpContent: {
      description: 'Connect to the Access Agentic Platform for AI agent invocation and analytics. This integration allows you to invoke AI agents, retrieve analytics data, and manage agent interactions.',
      setupSteps: [
        'Obtain your API Key from your Access Agentic Platform administrator',
        'Get the Base URL for your environment (e.g., https://peoplexd-agenticrostermiddleware-qa.azurewebsites.net)',
        'Enter the API Key and Base URL in the fields below',
        'Test the connection to verify your credentials',
      ],
      requiredFields: ['API Key', 'Base URL'],
      tips: [
        'The API Key is sent in the X-API-Key header for all requests',
        'Use the correct Base URL for your environment (QA, UAT, Production)',
        'Contact your Access administrator if you need API access',
      ],
    },
  },
  'donorfy': {
    type: 'donorfy',
    label: 'Donorfy',
    description: 'Donorfy CRM integration',
    category: 'access-integrations',
    icon: Users,
    iconType: 'lucide',
    ...colorMap['rose'],
    tags: ['donorfy', 'charity', 'crm', 'fundraising', 'nonprofit'],
    testable: true,
    helpContent: {
      description: 'Connect to Donorfy for charity CRM and fundraising operations. You can find your Tenant ID and API key in Donorfy under Settings > API Settings. API access must be enabled by Donorfy Support (Professional feature).',
      setupSteps: [
        'Log in to your Donorfy account',
        'Navigate to Settings > API Settings',
        'Request API access from Donorfy Support if not already enabled',
        'Copy your Tenant ID (the code in your API URL, e.g., X4D8DZ1NH6)',
        'Generate or copy your API Key (the secret used for authentication)',
      ],
      requiredFields: ['Tenant ID', 'API Key'],
      links: [
        { label: 'Donorfy API Documentation', url: 'https://help.donorfy.com/en/' },
      ],
      tips: [
        'The Tenant ID is your organization code found in API URLs (e.g., data.donorfy.com/api/v1/X4D8DZ1NH6/...)',
        'The API Key is the secret password used for Basic Auth',
        'The username field is optional and defaults to "EvoWorkflow"',
        'Rate limit: 301 requests per minute. Exceeding this will temporarily disable API access.',
      ],
    },
  },
  'paycircle': {
    type: 'paycircle',
    label: 'PayCircle',
    description: 'PayCircle payroll integration',
    category: 'access-integrations',
    icon: Banknote,
    iconType: 'lucide',
    ...colorMap['green'],
    tags: ['paycircle', 'payroll', 'hr', 'finance'],
    testable: true,
    helpContent: {
      description: 'Connect to PayCircle for payroll and HR data integration. API credentials are provided by your PayCircle Account Manager or Support Team. Credentials provide bureau-level access to all companies within your bureau.',
      setupSteps: [
        'Contact your PayCircle Account Manager or Support Team',
        'Request API credentials for your bureau',
        'Enter your username and password',
        'Configure the base URL if using a custom endpoint (optional)',
      ],
      requiredFields: ['Username', 'Password'],
      tips: [
        'API credentials provide bureau-level access to all companies within your bureau',
        'Leave the Base URL field empty to use the default PayCircle API endpoint',
      ],
    },
  },
  'peoplehr': {
    type: 'peoplehr',
    label: 'PeopleHR',
    description: 'PeopleHR integration',
    category: 'access-integrations',
    icon: Users,
    iconType: 'lucide',
    ...colorMap['cyan'],
    tags: ['peoplehr', 'hr', 'people', 'workforce'],
    testable: true,
    helpContent: {
      description: 'Connect to PeopleHR for HR and employee data integration. You can find your API key in PeopleHR under Settings > API > API Key.',
      setupSteps: [
        'Log in to your PeopleHR account',
        'Navigate to Settings > API > API Key',
        'Generate or copy your API key',
      ],
      requiredFields: ['API Key'],
      links: [
        { label: 'PeopleHR API Documentation', url: 'https://apidocs.peoplehr.com/' },
      ],
      tips: [
        'Rate limit: 30 requests per minute per IP. Exceeding this will temporarily disable API access.',
      ],
    },
  },

  // ---- Productivity & Project Management ----
  'asana_oauth2': {
    type: 'asana_oauth2',
    label: 'Asana',
    description: 'Asana project management OAuth2',
    category: 'general',
    icon: SiAsana,
    iconType: 'simple-icon',
    iconColor: '#F06A6A',
    ...colorMap['rose'],
    tags: ['asana', 'project', 'management', 'tasks', 'oauth', 'productivity'],
    testable: true,
    helpContent: {
      description: 'Connect to Asana for project and task management using OAuth2 authentication.',
      setupSteps: [
        'Go to the Asana Developer Console (https://app.asana.com/0/developer-console)',
        'Create a new application',
        'Note the Client ID and Client Secret',
        'Configure the Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Complete the OAuth flow to authorize',
      ],
      requiredFields: ['Client ID', 'Client Secret'],
      links: [
        { label: 'Asana Developer Console', url: 'https://app.asana.com/0/developer-console' },
        { label: 'Asana API Documentation', url: 'https://developers.asana.com/docs' },
      ],
      tips: [
        'Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Keep your client secret secure',
        'Use appropriate scopes for the access level you need',
      ],
    },
  },

  // ---- AI Services ----
  // Note: Access LLM uses platform system credential (litellm-system) - no user credential type needed
  'claude': {
    type: 'claude',
    label: 'Anthropic Claude',
    description: 'Anthropic Claude AI API',
    category: 'ai-services',
    icon: SiAnthropic,
    iconType: 'simple-icon',
    iconColor: '#D97757',
    ...colorMap['orange'],
    tags: ['anthropic', 'claude', 'ai', 'llm', 'chat'],
    testable: true,
    helpContent: {
      description: 'Connect to Anthropic\'s Claude AI models for natural language processing.',
      setupSteps: [
        'Sign up at console.anthropic.com',
        'Navigate to API Keys section',
        'Create a new API key',
        'Copy and paste the key below',
      ],
      requiredFields: ['API Key'],
      links: [
        { label: 'Anthropic Console', url: 'https://console.anthropic.com/' },
        { label: 'API Documentation', url: 'https://docs.anthropic.com/' },
      ],
      tips: ['Keep your API key secure - it provides full access to your account'],
    },
  },
  'openai': {
    type: 'openai',
    label: 'OpenAI',
    description: 'OpenAI API (GPT, DALL-E, etc.)',
    category: 'ai-services',
    icon: SiOpenai,
    iconType: 'simple-icon',
    iconColor: '#412991',
    ...colorMap['violet'],
    tags: ['openai', 'gpt', 'ai', 'llm', 'chat', 'dalle'],
    testable: true,
    helpContent: {
      description: 'Connect to OpenAI for GPT, DALL-E, and other AI models.',
      setupSteps: [
        'Sign up at platform.openai.com',
        'Navigate to API Keys in your account settings',
        'Create a new secret key',
        'Copy and paste the key below',
      ],
      requiredFields: ['API Key'],
      links: [
        { label: 'OpenAI Platform', url: 'https://platform.openai.com/' },
        { label: 'API Documentation', url: 'https://platform.openai.com/docs/' },
      ],
      tips: ['Set up usage limits in your OpenAI account to control costs'],
    },
  },
  'azure-openai': {
    type: 'azure-openai',
    label: 'Azure OpenAI',
    description: 'Azure OpenAI Service',
    category: 'ai-services',
    icon: AzureIcon,
    iconType: 'microsoft-icon',
    ...colorMap['blue'],
    tags: ['azure', 'openai', 'microsoft', 'ai', 'llm'],
    testable: true,
    helpContent: {
      description: 'Connect to Azure OpenAI Service for enterprise AI capabilities.',
      setupSteps: [
        'Create an Azure OpenAI resource in the Azure Portal',
        'Deploy a model in Azure OpenAI Studio',
        'Copy your endpoint URL and API key from the resource',
      ],
      requiredFields: ['Endpoint', 'API Key'],
      links: [
        { label: 'Azure OpenAI Documentation', url: 'https://learn.microsoft.com/azure/ai-services/openai/' },
      ],
      tips: ['The endpoint URL format is: https://YOUR-RESOURCE-NAME.openai.azure.com'],
    },
  },

  // ---- Databases ----
  'msSqlCredential': {
    type: 'msSqlCredential',
    label: 'MS SQL Server',
    description: 'Microsoft SQL Server',
    category: 'databases',
    icon: SqlServerIcon,
    iconType: 'microsoft-icon',
    ...colorMap['red'],
    tags: ['sql', 'server', 'microsoft', 'mssql', 'database'],
    testable: true,
    helpContent: {
      description: 'Connect to Microsoft SQL Server databases using SQL Server or Windows Authentication.',
      setupSteps: [
        'Obtain the server hostname or IP address',
        'Get the database name you want to connect to',
        'Create or obtain SQL Server credentials (username/password)',
        'Ensure network connectivity to SQL Server (default port 1433)',
        'For Windows Authentication, specify the domain in Advanced Settings',
      ],
      requiredFields: ['Server', 'Database', 'Username', 'Password'],
      tips: [
        'Default port is 1433 - only specify if using a non-standard port',
        'Use "Trust Server Certificate" for self-signed or development certificates',
        'Enable encryption for secure connections over public networks',
        'For Windows Authentication, use domain\\username format',
      ],
    },
  },
  'postgresql': {
    type: 'postgresql',
    label: 'PostgreSQL',
    description: 'PostgreSQL database',
    category: 'databases',
    icon: SiPostgresql,
    iconType: 'simple-icon',
    iconColor: '#4169E1',
    ...colorMap['blue'],
    tags: ['postgresql', 'postgres', 'database', 'sql'],
    testable: true,
    helpContent: {
      description: 'Connect to PostgreSQL databases.',
      setupSteps: [
        'Obtain the host address',
        'Get the database name',
        'Create or obtain PostgreSQL credentials',
        'Ensure network connectivity (default port 5432)',
      ],
      requiredFields: ['Host', 'Database', 'Username', 'Password'],
      links: [
        { label: 'PostgreSQL Documentation', url: 'https://www.postgresql.org/docs/' },
      ],
      tips: ['Configure SSL mode based on your security requirements'],
    },
  },
  'mysql': {
    type: 'mysql',
    label: 'MySQL',
    description: 'MySQL database',
    category: 'databases',
    icon: SiMysql,
    iconType: 'simple-icon',
    iconColor: '#4479A1',
    ...colorMap['blue'],
    tags: ['mysql', 'database', 'sql', 'mariadb'],
    testable: true,
    helpContent: {
      description: 'Connect to MySQL or MariaDB databases.',
      setupSteps: [
        'Obtain the host address',
        'Get the database name',
        'Create or obtain MySQL credentials',
        'Ensure network connectivity (default port 3306)',
      ],
      requiredFields: ['Host', 'Database', 'Username', 'Password'],
      links: [
        { label: 'MySQL Documentation', url: 'https://dev.mysql.com/doc/' },
      ],
    },
  },
  'mongodb': {
    type: 'mongodb',
    label: 'MongoDB',
    description: 'MongoDB database',
    category: 'databases',
    icon: SiMongodb,
    iconType: 'simple-icon',
    iconColor: '#47A248',
    ...colorMap['green'],
    tags: ['mongodb', 'mongo', 'nosql', 'database', 'document'],
    testable: true,
    helpContent: {
      description: 'Connect to MongoDB databases using a connection string or individual fields.',
      setupSteps: [
        'Choose connection method: Connection String or Individual Fields',
        'For Connection String: paste your full MongoDB URI',
        'For Individual Fields: enter host, port, database, and credentials',
        'Configure advanced settings if needed (auth source, replica set, SSL)',
      ],
      requiredFields: ['Connection String or Host'],
      links: [
        { label: 'MongoDB Documentation', url: 'https://docs.mongodb.com/' },
        { label: 'MongoDB Atlas', url: 'https://cloud.mongodb.com/' },
      ],
      tips: [
        'Connection string format: mongodb://username:password@host:port/database',
        'For MongoDB Atlas use: mongodb+srv://username:password@cluster.mongodb.net/database',
        'Auth source is typically "admin" for user databases',
        'Enable SSL/TLS for production connections',
      ],
    },
  },
  'redis': {
    type: 'redis',
    label: 'Redis',
    description: 'Redis cache/database',
    category: 'databases',
    icon: SiRedis,
    iconType: 'simple-icon',
    iconColor: '#DC382D',
    ...colorMap['red'],
    tags: ['redis', 'cache', 'nosql', 'key-value'],
    testable: true,
    helpContent: {
      description: 'Connect to Redis servers for caching, sessions, and data storage.',
      setupSteps: [
        'Obtain the Redis server hostname or IP address',
        'Note the port number (default: 6379)',
        'Get the password if authentication is enabled',
        'Determine which database index to use (0-15)',
      ],
      requiredFields: ['Host'],
      links: [
        { label: 'Redis Documentation', url: 'https://redis.io/docs/' },
      ],
      tips: [
        'Default port is 6379',
        'Database index ranges from 0 to 15 (default: 0)',
        'Enable SSL/TLS for secure connections over public networks',
        'Use rediss:// protocol for SSL connections',
      ],
    },
  },
  'turso': {
    type: 'turso',
    label: 'Turso',
    description: 'Turso/LibSQL database',
    category: 'databases',
    icon: Database,
    iconType: 'lucide',
    ...colorMap['cyan'],
    tags: ['turso', 'libsql', 'sqlite', 'edge', 'database'],
    testable: true,
    helpContent: {
      description: 'Connect to Turso edge databases (LibSQL).',
      setupSteps: [
        'Create a database at turso.tech',
        'Get your database URL from the Turso dashboard',
        'Generate an authentication token',
      ],
      requiredFields: ['Database URL', 'Auth Token'],
      links: [
        { label: 'Turso Documentation', url: 'https://docs.turso.tech/' },
      ],
      tips: ['Database URL format: libsql://your-database.turso.io'],
    },
  },
  'elasticsearchApi': {
    type: 'elasticsearchApi',
    label: 'Elasticsearch',
    description: 'Elasticsearch search engine',
    category: 'databases',
    icon: Database,
    iconType: 'lucide',
    iconColor: undefined,
    ...colorMap['yellow'],
    tags: ['elasticsearch', 'elastic', 'search', 'nosql'],
    testable: true,
    helpContent: {
      description: 'Connect to Elasticsearch clusters for full-text search and analytics.',
      setupSteps: [
        'Get your Elasticsearch cluster URL (e.g., http://localhost:9200)',
        'Choose authentication: Basic Auth (username/password) or API Key',
        'For Basic Auth: use your Elasticsearch username and password',
        'For API Key: generate one from Kibana Stack Management > API Keys',
      ],
      requiredFields: ['Base URL'],
      links: [
        { label: 'Elasticsearch Documentation', url: 'https://www.elastic.co/guide/' },
        { label: 'Elastic Cloud', url: 'https://cloud.elastic.co/' },
      ],
      tips: [
        'API Key authentication is recommended for production',
        'API Key format: Base64 encoded id:api_key',
        'Only enable "Ignore SSL Issues" for development environments',
        'Default port is 9200 for HTTP, 9243 for Elastic Cloud',
      ],
    },
  },
  'databricks': {
    type: 'databricks',
    label: 'Databricks',
    description: 'Databricks SQL and data analytics platform',
    category: 'databases',
    icon: Database,
    iconType: 'lucide',
    iconColor: undefined,
    ...colorMap['red'],
    tags: ['databricks', 'sql', 'spark', 'data', 'analytics', 'lakehouse', 'azure', 'aws'],
    testable: true,
    helpContent: {
      description: 'Connect to Databricks workspaces to execute SQL queries and interact with data.',
      setupSteps: [
        'Log in to your Databricks workspace',
        'Go to User Settings > Developer > Access Tokens',
        'Click "Generate New Token" and copy the token',
        'Copy your workspace URL from the browser (e.g., https://adb-xxx.xx.azuredatabricks.net)',
        'Optionally, find your SQL Warehouse ID in SQL Warehouses section',
      ],
      requiredFields: ['Workspace URL', 'Personal Access Token'],
      links: [
        { label: 'Databricks Documentation', url: 'https://docs.databricks.com/' },
        { label: 'SQL Statement Execution API', url: 'https://docs.databricks.com/api/workspace/statementexecution' },
      ],
      tips: [
        'Personal access tokens can be scoped for specific permissions',
        'Tokens have expiration dates - set reminders to rotate them',
        'Workspace URL format: https://adb-<workspace-id>.<region>.azuredatabricks.net',
        'For AWS: https://<workspace-id>.cloud.databricks.com',
        'Set a default Warehouse ID to avoid specifying it on every node',
      ],
    },
  },

  // ---- Cloud Providers ----
  'aws': {
    type: 'aws',
    label: 'AWS',
    description: 'Amazon Web Services',
    category: 'cloud',
    icon: Cloud,
    iconType: 'lucide',
    iconColor: undefined,
    ...colorMap['orange'],
    tags: ['aws', 'amazon', 'cloud', 's3', 'lambda'],
    testable: false,
    helpContent: {
      description: 'Connect to Amazon Web Services.',
      setupSteps: [
        'Create an IAM user in AWS Console',
        'Generate access keys for the user',
        'Copy the Access Key ID and Secret Access Key',
        'Select your default region',
      ],
      requiredFields: ['Access Key ID', 'Secret Access Key', 'Region'],
      links: [
        { label: 'AWS IAM Documentation', url: 'https://docs.aws.amazon.com/IAM/' },
      ],
      tips: ['Use IAM roles with minimal required permissions'],
    },
  },
  'gcp': {
    type: 'gcp',
    label: 'Google Cloud',
    description: 'Google Cloud Platform',
    category: 'cloud',
    icon: SiGooglecloud,
    iconType: 'simple-icon',
    iconColor: '#4285F4',
    ...colorMap['blue'],
    tags: ['gcp', 'google', 'cloud', 'firebase'],
    testable: false,
    helpContent: {
      description: 'Connect to Google Cloud Platform.',
      setupSteps: [
        'Create a service account in GCP Console',
        'Download the JSON key file',
        'Copy the project ID',
        'Paste the service account key JSON',
      ],
      requiredFields: ['Project ID', 'Service Account Key'],
      links: [
        { label: 'GCP Documentation', url: 'https://cloud.google.com/docs/' },
      ],
      tips: ['Service account keys are sensitive - store them securely'],
    },
  },

  // ---- Microsoft 365 ----
  'microsoft_teams_oauth': {
    type: 'microsoft_teams_oauth',
    label: 'Microsoft Teams',
    description: 'Microsoft Teams OAuth2',
    category: 'microsoft',
    icon: MicrosoftTeamsIcon,
    iconType: 'microsoft-icon',
    ...colorMap['purple'],
    tags: ['microsoft', 'teams', 'oauth', 'chat', 'collaboration'],
    testable: true,
    helpContent: {
      description: 'Connect to Microsoft Teams using OAuth2 for messaging and collaboration.',
      setupSteps: [
        'Go to Azure Portal → App Registrations',
        'Create a new application registration',
        'Note the Application (Client) ID and Directory (Tenant) ID',
        'Create a Client Secret under Certificates & secrets',
        'Under Authentication, add the Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Add required API permissions under API permissions',
        'Grant admin consent if required',
        'Save the credential and complete the OAuth flow',
      ],
      requiredFields: ['Tenant ID', 'Client ID', 'Client Secret'],
      links: [
        { label: 'Azure Portal - App Registrations', url: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade' },
        { label: 'Teams API Documentation', url: 'https://docs.microsoft.com/graph/teams-concept-overview' },
      ],
      tips: [
        'Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Required permissions: User.Read, Chat.ReadWrite, ChannelMessage.Send',
        'For team management: Group.ReadWrite.All, Team.ReadBasic.All',
        'For file uploads: Files.ReadWrite.All',
        'For meetings: Calendars.ReadWrite',
        'Always include offline_access for refresh tokens',
        'Some permissions require admin consent',
      ],
    },
  },
  'microsoft_outlook_oauth': {
    type: 'microsoft_outlook_oauth',
    label: 'Microsoft Outlook',
    description: 'Microsoft Outlook OAuth2',
    category: 'microsoft',
    icon: MicrosoftOutlookIcon,
    iconType: 'microsoft-icon',
    ...colorMap['blue'],
    tags: ['microsoft', 'outlook', 'email', 'oauth', 'mail'],
    testable: true,
    helpContent: {
      description: 'Connect to Microsoft Outlook using OAuth2 for email operations.',
      setupSteps: [
        'Go to Azure Portal → App Registrations',
        'Create a new application registration',
        'Note the Application (Client) ID and Directory (Tenant) ID',
        'Create a Client Secret under Certificates & secrets',
        'Under Authentication, add the Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Add required API permissions under API permissions',
        'Grant admin consent if required',
        'Save the credential and complete the OAuth flow',
      ],
      requiredFields: ['Tenant ID', 'Client ID', 'Client Secret'],
      links: [
        { label: 'Azure Portal - App Registrations', url: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade' },
        { label: 'Outlook Mail API', url: 'https://docs.microsoft.com/graph/outlook-mail-concept-overview' },
      ],
      tips: [
        'Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Required permissions: User.Read, Mail.ReadWrite, Mail.Send',
        'For calendar: Calendars.ReadWrite',
        'For contacts: Contacts.ReadWrite',
        'For mailbox settings: MailboxSettings.ReadWrite',
        'Always include offline_access for refresh tokens',
        'Enable "Shared Mailbox" option to access shared mailboxes',
      ],
    },
  },
  'microsoft_sharepoint_oauth': {
    type: 'microsoft_sharepoint_oauth',
    label: 'Microsoft SharePoint',
    description: 'Microsoft SharePoint OAuth2',
    category: 'microsoft',
    icon: SharePointIcon,
    iconType: 'microsoft-icon',
    ...colorMap['teal'],
    tags: ['microsoft', 'sharepoint', 'oauth', 'files', 'documents'],
    testable: true,
    helpContent: {
      description: 'Connect to Microsoft SharePoint using OAuth2 for document management.',
      setupSteps: [
        'Go to Azure Portal → App Registrations',
        'Create a new application registration',
        'Note the Application (Client) ID and Directory (Tenant) ID',
        'Create a Client Secret under Certificates & secrets',
        'Under Authentication, add the Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Grant SharePoint API permissions (Sites.ReadWrite.All, Files.ReadWrite.All)',
        'Complete the OAuth flow to authorize',
      ],
      requiredFields: ['Tenant ID', 'Client ID', 'Client Secret'],
      links: [
        { label: 'Azure Portal - App Registrations', url: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade' },
        { label: 'SharePoint API Documentation', url: 'https://docs.microsoft.com/sharepoint/dev/' },
      ],
      tips: [
        'Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Required permissions: Sites.ReadWrite.All, Files.ReadWrite.All',
        'Always include offline_access for refresh tokens',
      ],
    },
  },
  'microsoft_onedrive_oauth': {
    type: 'microsoft_onedrive_oauth',
    label: 'Microsoft OneDrive',
    description: 'Microsoft OneDrive OAuth2',
    category: 'microsoft',
    icon: OneDriveIcon,
    iconType: 'microsoft-icon',
    ...colorMap['blue'],
    tags: ['microsoft', 'onedrive', 'oauth', 'files', 'storage'],
    testable: true,
    helpContent: {
      description: 'Connect to Microsoft OneDrive using OAuth2 for file storage.',
      setupSteps: [
        'Go to Azure Portal → App Registrations',
        'Create a new application registration',
        'Note the Application (Client) ID and Directory (Tenant) ID',
        'Create a Client Secret under Certificates & secrets',
        'Under Authentication, add the Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Grant Microsoft Graph permissions for Files (Files.ReadWrite.All)',
        'Complete the OAuth flow to authorize',
      ],
      requiredFields: ['Tenant ID', 'Client ID', 'Client Secret'],
      links: [
        { label: 'Azure Portal - App Registrations', url: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade' },
        { label: 'OneDrive API Documentation', url: 'https://docs.microsoft.com/graph/onedrive-concept-overview' },
      ],
      tips: [
        'Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Required permissions: Files.ReadWrite.All',
        'Always include offline_access for refresh tokens',
      ],
    },
  },
  'microsoft_entra_oauth': {
    type: 'microsoft_entra_oauth',
    label: 'Microsoft Entra ID',
    description: 'Microsoft Entra ID (formerly Azure AD) OAuth2',
    category: 'microsoft',
    icon: EntraIcon,
    iconType: 'microsoft-icon',
    ...colorMap['blue'],
    tags: ['microsoft', 'entra', 'azure', 'azure-ad', 'oauth', 'cloud', 'directory', 'users', 'groups'],
    testable: true,
    helpContent: {
      description: 'Connect to Microsoft Entra ID (formerly Azure AD) using OAuth2 via the Microsoft Graph API.',
      setupSteps: [
        'Go to Azure Portal → App Registrations',
        'Create a new application registration',
        'Note the Application (Client) ID and Directory (Tenant) ID',
        'Create a Client Secret under Certificates & secrets',
        'Under Authentication, add the Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Add required API permissions under API permissions',
        'Grant admin consent for directory permissions',
        'Save the credential and complete the OAuth flow',
      ],
      requiredFields: ['Tenant ID', 'Client ID', 'Client Secret'],
      links: [
        { label: 'Azure Portal - App Registrations', url: 'https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade' },
        { label: 'Microsoft Graph API Documentation', url: 'https://docs.microsoft.com/graph/overview' },
        { label: 'Entra ID Documentation', url: 'https://learn.microsoft.com/entra/identity/' },
      ],
      tips: [
        'Redirect URI: {backend-url}/api/credentials/oauth/callback',
        'Required permissions: User.Read.All, Group.Read.All',
        'For group membership: GroupMember.Read.All',
        'Always include offline_access for refresh tokens',
        'Directory permissions typically require admin consent',
        'This replaces the legacy Azure AD credential type',
      ],
    },
  },

  // ---- File Transfer ----
  'ftp': {
    type: 'ftp',
    label: 'FTP / SFTP',
    description: 'FTP or SFTP file transfer',
    category: 'file-transfer',
    icon: Server,
    iconType: 'lucide',
    ...colorMap['gray'],
    tags: ['ftp', 'sftp', 'file', 'transfer', 'ssh'],
    testable: true,
    helpContent: {
      description: 'Connect to FTP or SFTP servers for secure file transfers.',
      setupSteps: [
        'Choose protocol: FTP (unencrypted) or SFTP (SSH-based secure)',
        'Enter the server hostname or IP address',
        'Verify the port (FTP: 21, SFTP: 22)',
        'For FTP: Choose anonymous or provide username/password',
        'For SFTP: Use password or private key authentication',
      ],
      requiredFields: ['Protocol', 'Host', 'Username (for authenticated access)'],
      tips: [
        'SFTP (port 22) is recommended for secure file transfers',
        'Private key authentication is more secure than passwords',
        'For FTP, enable SSL (FTPS) for encrypted transfers',
        'Use passive mode if behind a firewall',
        'Default timeout is 60 seconds',
      ],
    },
  },
  'azure_blob_storage': {
    type: 'azure_blob_storage',
    label: 'Azure Blob Storage',
    description: 'Azure Blob Storage',
    category: 'file-transfer',
    icon: AzureBlobStorageIcon,
    iconType: 'microsoft-icon',
    ...colorMap['blue'],
    tags: ['azure', 'blob', 'storage', 'cloud', 'files'],
    testable: true,
    helpContent: {
      description: 'Connect to Azure Blob Storage for cloud file storage and management.',
      setupSteps: [
        'Navigate to Azure Portal > Storage Account',
        'Choose an authentication method:',
        'Storage Account Key: Go to Access Keys, copy account name and key',
        'Connection String: Copy the full connection string from Access Keys',
        'SAS Token: Go to Shared access signature, configure permissions and generate',
      ],
      requiredFields: ['Authentication Method', 'Account Name or Connection String'],
      links: [
        { label: 'Azure Blob Storage Documentation', url: 'https://docs.microsoft.com/azure/storage/blobs/' },
        { label: 'Azure Portal', url: 'https://portal.azure.com/' },
      ],
      tips: [
        'Storage Account Key provides full access - keep it secure',
        'Use SAS Tokens for time-limited, scoped access',
        'Connection String includes all required connection info',
        'SAS Tokens can be configured with specific permissions (read, write, delete)',
      ],
    },
  },

  // ---- Email ----
  'smtp': {
    type: 'smtp',
    label: 'SMTP Email',
    description: 'SMTP email server',
    category: 'email',
    icon: Mail,
    iconType: 'lucide',
    ...colorMap['amber'],
    tags: ['smtp', 'email', 'mail', 'send'],
    testable: true,
    helpContent: {
      description: 'Configure SMTP settings for sending emails.',
      setupSteps: [
        'Get your SMTP server hostname',
        'Determine the port (587 for TLS, 465 for SSL, 25 for unencrypted)',
        'Enter your email credentials',
      ],
      requiredFields: ['Host', 'Port'],
      tips: [
        'Port 587 with STARTTLS is recommended for most providers',
        'Gmail requires an App Password if 2FA is enabled',
      ],
    },
  },
  'sendgrid_api': {
    type: 'sendgrid_api',
    label: 'SendGrid API',
    description: 'SendGrid Email API',
    category: 'email',
    icon: Mail,
    iconType: 'lucide',
    ...colorMap['blue'],
    tags: ['sendgrid', 'email', 'mail', 'send', 'api', 'transactional'],
    testable: true,
    helpContent: {
      description: 'Connect to SendGrid using their Web API v3 for reliable email delivery.',
      setupSteps: [
        'Sign in to your SendGrid account at app.sendgrid.com',
        'Navigate to Settings → API Keys',
        'Click "Create API Key"',
        'Give it a name and select "Full Access" or "Restricted Access" with Mail Send permissions',
        'Copy the API key (it will only be shown once)',
        'Paste the API key below',
      ],
      requiredFields: ['API Key'],
      links: [
        { label: 'SendGrid Dashboard', url: 'https://app.sendgrid.com/' },
        { label: 'API Documentation', url: 'https://docs.sendgrid.com/api-reference/mail-send/mail-send' },
      ],
      tips: [
        'Keep your API key secure and never share it publicly',
        'Use restricted access with only Mail Send permissions for better security',
        'Verify your sender email address in SendGrid before sending',
        'Set up domain authentication for better deliverability',
      ],
    },
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get credential definition by type
 */
export function getCredentialDefinition(type: string): CredentialDefinition {
  return CREDENTIAL_DEFINITIONS[type] || {
    type,
    label: type,
    description: 'Unknown credential type',
    category: 'general',
    icon: Key,
    iconType: 'lucide',
    ...colorMap['gray'],
    tags: [],
    testable: false,
    helpContent: {
      description: 'No documentation available for this credential type.',
      setupSteps: [],
      requiredFields: [],
    },
  };
}

/**
 * Get all credentials for a specific category
 */
export function getCredentialsByCategory(category: CredentialCategory): CredentialDefinition[] {
  return Object.values(CREDENTIAL_DEFINITIONS).filter(def => def.category === category);
}

/**
 * Get all credential definitions
 */
export function getAllCredentialDefinitions(): CredentialDefinition[] {
  return Object.values(CREDENTIAL_DEFINITIONS);
}

/**
 * Search credentials by query string
 */
export function searchCredentials(query: string): CredentialDefinition[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) {
    return getAllCredentialDefinitions();
  }

  return getAllCredentialDefinitions().filter(def =>
    def.label.toLowerCase().includes(lowerQuery) ||
    def.description.toLowerCase().includes(lowerQuery) ||
    def.tags.some(tag => tag.toLowerCase().includes(lowerQuery))
  );
}

/**
 * Get credential type display label
 */
export function getCredentialTypeLabel(type: string): string {
  return getCredentialDefinition(type).label;
}

/**
 * Get all category keys
 */
export function getCredentialCategoryKeys(): CredentialCategory[] {
  return Object.keys(CREDENTIAL_CATEGORIES) as CredentialCategory[];
}
